import { resumeCondition } from './conditions-editeur.ts'
import type { ComponentChildren } from 'preact'
import { useEffect, useRef, useState } from 'preact/hooks'
import type { Bloc, GrapheParcours } from '@funo/grammaire'

type Point = { x: number; y: number }
type Camera = Point & { zoom: number }
type Port = { cle: string; type: 'entree' | 'sortie' }
type Lien = GrapheParcours['liens'][number]
type Geste =
  | { type: 'pan'; id: number; debut: Point; camera: Camera; bouge: boolean }
  | {
      type: 'noeud'
      id: number
      debut: Point
      position: Point
      cle: string
      zoom: number
      bouge: boolean
    }
  | { type: 'lien'; id: number; port: Port; precedent: Port | null }
export interface PropsCanevasParcours {
  blocs: Bloc[]
  graphe: GrapheParcours
  selection: string
  lecture?: boolean
  onSelection(cle: string): void
  onGraphe(graphe: GrapheParcours): void
  onAjouter(type: Bloc['bloc'], position: Point): void
  onSupprimer(cle: string): void
  onDupliquer(cle: string): void
  noms: Record<Bloc['bloc'], string>
  description(bloc: Bloc): string
  icone(type: Bloc['bloc']): ComponentChildren
}
const LARGEUR = 248,
  HAUTEUR = 184,
  PORT_Y = 79
// Familles tirées du guide Funo : violets pour la collecte, prune pour la
// réservation, mûre pour l'engagement, tons chauds pour l'argent et la décision.
const ACCENTS: Record<Bloc['bloc'], string> = {
  identite: '#5b5dd6',
  questions: '#7a5bc4',
  creneau: '#391d44',
  offres: '#6b3d7a',
  contrat: '#8a3f78',
  signature: '#a64d86',
  approbation: '#9a6412',
  paiement: '#2f7a64',
  depot: '#3d7087',
  notification: '#a0561f',
  confirmation: '#3f7d3a',
}
const limiterZoom = (z: number) => Math.min(2, Math.max(0.16, z))
const nomDe = (
  blocs: Bloc[],
  noms: PropsCanevasParcours['noms'],
  cle: string,
) => {
  const b = blocs.find((b) => b.cle === cle)
  return b ? b.titre || noms[b.bloc] : 'Étape retirée'
}
function positionParDefaut(index: number): Point {
  return { x: 80 + (index % 4) * 326, y: 100 + Math.floor(index / 4) * 250 }
}
function courbe(de: Point, vers: Point): string {
  const ecart = Math.max(65, Math.min(180, Math.abs(vers.x - de.x) * 0.5))
  return `M ${de.x} ${de.y} C ${de.x + ecart} ${de.y}, ${vers.x - ecart} ${vers.y}, ${vers.x} ${vers.y}`
}
function Svg({
  type,
}: {
  type:
    | 'main'
    | 'curseur'
    | 'adapter'
    | 'ranger'
    | 'supprimer'
    | 'dupliquer'
    | 'depart'
    | 'fermer'
}) {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="1.7"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      {type === 'main' ? (
        <path d="M7 11V6a2 2 0 0 1 4 0v5-7a2 2 0 0 1 4 0v7-5a2 2 0 0 1 4 0v9c0 4-3 7-7 7h-1c-3 0-4-2-6-5l-2-4a2 2 0 0 1 3-2l2 2" />
      ) : type === 'curseur' ? (
        <path d="m5 3 15 10-7 1-3 7Z" />
      ) : type === 'adapter' ? (
        <>
          <path d="M8 3H3v5m13-5h5v5M3 16v5h5m13-5v5h-5" />
          <rect x="8" y="8" width="8" height="8" rx="1" />
        </>
      ) : type === 'ranger' ? (
        <>
          <rect x="2" y="3" width="6" height="6" rx="1" />
          <rect x="16" y="3" width="6" height="6" rx="1" />
          <rect x="9" y="15" width="6" height="6" rx="1" />
          <path d="M8 6h8M19 9v3H12v3" />
        </>
      ) : type === 'supprimer' ? (
        <>
          <path d="M4 7h16M9 7V4h6v3M6 7l1 14h10l1-14M10 11v6m4-6v6" />
        </>
      ) : type === 'dupliquer' ? (
        <>
          <rect x="8" y="8" width="12" height="12" rx="2" />
          <path d="M16 8V4H4v12h4" />
        </>
      ) : type === 'depart' ? (
        <path d="m8 4 12 8-12 8Z" />
      ) : (
        <path d="m6 6 12 12M6 18 18 6" />
      )}
    </svg>
  )
}

export function CanevasParcours(props: PropsCanevasParcours) {
  const {
    blocs,
    graphe,
    selection,
    lecture = false,
    onSelection,
    onGraphe,
    onAjouter,
    onSupprimer,
    onDupliquer,
    noms,
    description,
    icone,
  } = props
  const surface = useRef<HTMLDivElement>(null),
    geste = useRef<Geste | null>(null),
    donnees = useRef(props),
    cameraRef = useRef<Camera>({ x: 30, y: 25, zoom: 0.88 }),
    identifiant = useRef('cv-' + crypto.randomUUID().slice(0, 8)),
    initialise = useRef(false)
  const [camera, setCamera] = useState<Camera>(cameraRef.current),
    [outil, setOutil] = useState<'selection' | 'main'>('selection'),
    [taille, setTaille] = useState({ largeur: 900, hauteur: 620 }),
    [deplacement, setDeplacement] = useState<{
      cle: string
      position: Point
    } | null>(null)
  const [portActif, setPortActif] = useState<Port | null>(null),
    [pointeur, setPointeur] = useState<Point | null>(null),
    [lienSelectionne, setLienSelectionne] = useState<Lien | null>(null),
    [message, setMessage] = useState(''),
    [survolDepot, setSurvolDepot] = useState(false),
    [glissement, setGlissement] = useState(false)
  donnees.current = props
  cameraRef.current = camera
  const nom = (cle: string) => nomDe(blocs, noms, cle)
  function position(cle: string): Point {
    if (deplacement?.cle === cle) return deplacement.position
    return (
      graphe.positions[cle] ??
      positionParDefaut(
        Math.max(
          0,
          blocs.findIndex((b) => b.cle === cle),
        ),
      )
    )
  }
  function pointPort(port: Port): Point {
    const p = position(port.cle)
    return { x: p.x + (port.type === 'sortie' ? LARGEUR : 0), y: p.y + PORT_Y }
  }
  function monde(client: Point): Point {
    const r = surface.current!.getBoundingClientRect(),
      c = cameraRef.current
    return {
      x: (client.x - r.left - c.x) / c.zoom,
      y: (client.y - r.top - c.y) / c.zoom,
    }
  }
  function zoomer(z: number, ancre?: Point) {
    const c = cameraRef.current,
      nouveau = limiterZoom(z),
      point = ancre ?? { x: taille.largeur / 2, y: taille.hauteur / 2 }
    setCamera({
      zoom: nouveau,
      x: point.x - ((point.x - c.x) * nouveau) / c.zoom,
      y: point.y - ((point.y - c.y) * nouveau) / c.zoom,
    })
  }
  function adapter(positions = graphe.positions) {
    if (!surface.current) return
    const { width: w, height: h } = surface.current.getBoundingClientRect()
    if (!blocs.length) {
      setCamera({ x: 35, y: 35, zoom: 1 })
      return
    }
    const ps = blocs.map((b, i) => positions[b.cle] ?? positionParDefaut(i)),
      minX = Math.min(...ps.map((p) => p.x)),
      minY = Math.min(...ps.map((p) => p.y)),
      largeur = Math.max(...ps.map((p) => p.x)) + LARGEUR - minX,
      hauteur = Math.max(...ps.map((p) => p.y)) + HAUTEUR - minY
    const z = limiterZoom(Math.min(1, (w - 110) / largeur, (h - 110) / hauteur))
    setCamera({
      zoom: z,
      x: (w - largeur * z) / 2 - minX * z,
      y: (h - hauteur * z) / 2 - minY * z,
    })
  }
  useEffect(() => {
    const el = surface.current
    if (!el) return
    const observateur = new ResizeObserver(([entree]) => {
      if (!entree) return
      const w = entree.contentRect.width,
        h = entree.contentRect.height
      setTaille({ largeur: w, hauteur: h })
      if (!initialise.current && w > 0) {
        initialise.current = true
        const actuel = donnees.current,
          cle = actuel.graphe.entree ?? actuel.blocs[0]?.cle,
          p = cle
            ? (actuel.graphe.positions[cle] ??
              positionParDefaut(
                Math.max(
                  0,
                  actuel.blocs.findIndex((b) => b.cle === cle),
                ),
              ))
            : { x: 0, y: 0 },
          z = w < 450 ? 0.9 : 0.88
        setCamera({
          zoom: z,
          x: 45 - p.x * z,
          y: Math.min(120, Math.max(40, (h - HAUTEUR * z) / 2)) - p.y * z,
        })
      }
    })
    observateur.observe(el)
    const roulette = (e: WheelEvent) => {
      e.preventDefault()
      if (geste.current) return
      const c = cameraRef.current
      if (e.ctrlKey || e.metaKey) {
        const r = el.getBoundingClientRect(),
          ancre = { x: e.clientX - r.left, y: e.clientY - r.top },
          z = limiterZoom(c.zoom * Math.exp(-e.deltaY * 0.007))
        setCamera({
          zoom: z,
          x: ancre.x - ((ancre.x - c.x) * z) / c.zoom,
          y: ancre.y - ((ancre.y - c.y) * z) / c.zoom,
        })
      } else
        setCamera({
          ...c,
          x: c.x - e.deltaX - (e.shiftKey ? e.deltaY : 0),
          y: c.y - (e.shiftKey ? 0 : e.deltaY),
        })
    }
    el.addEventListener('wheel', roulette, { passive: false })
    return () => {
      observateur.disconnect()
      el.removeEventListener('wheel', roulette)
    }
  }, [])
  useEffect(() => {
    if (
      lienSelectionne &&
      !graphe.liens.some(
        (l) => l.de === lienSelectionne.de && l.vers === lienSelectionne.vers,
      )
    )
      setLienSelectionne(null)
    if (portActif && !blocs.some((b) => b.cle === portActif.cle)) {
      setPortActif(null)
      setPointeur(null)
    }
  }, [graphe, blocs])
  function capturer(e: PointerEvent) {
    surface.current?.setPointerCapture(e.pointerId)
  }
  function annuler() {
    geste.current = null
    setGlissement(false)
    setDeplacement(null)
    setPortActif(null)
    setPointeur(null)
  }
  function commencerPan(e: PointerEvent) {
    if (e.button !== 0 && e.button !== 1) return
    e.preventDefault()
    geste.current = {
      type: 'pan',
      id: e.pointerId,
      debut: { x: e.clientX, y: e.clientY },
      camera: cameraRef.current,
      bouge: false,
    }
    setGlissement(true)
    capturer(e)
  }
  function commencerNoeud(e: PointerEvent, cle: string) {
    if (e.button !== 0) return
    e.stopPropagation()
    if (outil === 'main') {
      commencerPan(e)
      return
    }
    onSelection(cle)
    setLienSelectionne(null)
    setPortActif(null)
    setPointeur(null)
    if (lecture) return
    e.preventDefault()
    geste.current = {
      type: 'noeud',
      id: e.pointerId,
      debut: { x: e.clientX, y: e.clientY },
      position: position(cle),
      cle,
      zoom: cameraRef.current.zoom,
      bouge: false,
    }
    setGlissement(true)
    capturer(e)
  }
  function commencerLien(e: PointerEvent, port: Port) {
    if (lecture || e.button !== 0) return
    e.stopPropagation()
    e.preventDefault()
    ;(e.currentTarget as HTMLElement).focus({ preventScroll: true })
    setLienSelectionne(null)
    setPortActif(port)
    setPointeur(pointPort(port))
    geste.current = {
      type: 'lien',
      id: e.pointerId,
      port,
      precedent: portActif,
    }
    capturer(e)
  }
  function connecter(un: Port, deux: Port) {
    if (lecture) return
    if (un.type === deux.type) {
      setMessage('Reliez une sortie, à droite, à une entrée, à gauche.')
      return
    }
    const de = un.type === 'sortie' ? un.cle : deux.cle,
      vers = un.type === 'entree' ? un.cle : deux.cle
    if (de === vers) {
      setMessage('Une étape ne peut pas être reliée à elle-même.')
      return
    }
    const actuel = donnees.current.graphe
    if (actuel.liens.some((l) => l.de === de && l.vers === vers)) {
      setMessage('Ces deux étapes sont déjà reliées.')
      setPortActif(null)
      setPointeur(null)
      return
    }
    const conserves = actuel.liens.filter(
        (l) => l.de !== de && l.vers !== vers,
      ),
      vus = new Set<string>(),
      reste = [vers]
    while (reste.length) {
      const cle = reste.pop()!
      if (cle === de) {
        setMessage(
          'Cette liaison créerait une boucle. Choisissez une étape qui vient après.',
        )
        return
      }
      if (vus.has(cle)) continue
      vus.add(cle)
      reste.push(...conserves.filter((l) => l.de === cle).map((l) => l.vers))
    }
    const remplaces = actuel.liens.length - conserves.length
    onGraphe({ ...actuel, liens: [...conserves, { de, vers }] })
    setPortActif(null)
    setPointeur(null)
    setLienSelectionne({ de, vers })
    setMessage(
      `« ${nom(de)} » mène maintenant à « ${nom(vers)} ».${remplaces ? ' La liaison précédente a été remplacée.' : ''}${actuel.entree === vers ? ' Choisissez le nouveau point de départ du parcours.' : ''}`,
    )
  }
  function choisirPort(port: Port) {
    if (lecture) return
    if (portActif) {
      if (portActif.cle === port.cle && portActif.type === port.type) {
        setPortActif(null)
        setPointeur(null)
        setMessage('Connexion annulée.')
      } else connecter(portActif, port)
    } else {
      setPortActif(port)
      setPointeur(null)
      setMessage(
        `Choisissez ${port.type === 'sortie' ? 'l’entrée de l’étape suivante' : 'la sortie de l’étape précédente'}. Échap pour annuler.`,
      )
    }
  }
  function bouger(e: PointerEvent) {
    const g = geste.current
    if (!g || g.id !== e.pointerId) return
    if (g.type === 'lien') {
      setPointeur(monde({ x: e.clientX, y: e.clientY }))
      return
    }
    const dx = e.clientX - g.debut.x,
      dy = e.clientY - g.debut.y
    if (g.type === 'pan') {
      g.bouge ||= Math.abs(dx) + Math.abs(dy) > 3
      setCamera({ ...g.camera, x: g.camera.x + dx, y: g.camera.y + dy })
    } else {
      g.bouge ||= Math.abs(dx) + Math.abs(dy) > 3
      setDeplacement({
        cle: g.cle,
        position: {
          x: Math.round(g.position.x + dx / g.zoom),
          y: Math.round(g.position.y + dy / g.zoom),
        },
      })
    }
  }
  function terminer(e: PointerEvent) {
    const g = geste.current
    if (!g || g.id !== e.pointerId) return
    geste.current = null
    setGlissement(false)
    if (g.type === 'noeud') {
      if (g.bouge) {
        const p = {
            x: Math.round(g.position.x + (e.clientX - g.debut.x) / g.zoom),
            y: Math.round(g.position.y + (e.clientY - g.debut.y) / g.zoom),
          },
          actuel = donnees.current.graphe
        if (donnees.current.blocs.some((b) => b.cle === g.cle))
          onGraphe({
            ...actuel,
            positions: { ...actuel.positions, [g.cle]: p },
          })
      }
      setDeplacement(null)
    } else if (g.type === 'pan') {
      if (!g.bouge) {
        setLienSelectionne(null)
        setPortActif(null)
        setPointeur(null)
      }
    } else {
      const cible = document
        .elementFromPoint(e.clientX, e.clientY)
        ?.closest<HTMLElement>('[data-cv-port]')
      if (
        cible?.dataset.cle &&
        cible.dataset.cvPort &&
        (cible.dataset.cle !== g.port.cle ||
          cible.dataset.cvPort !== g.port.type)
      )
        connecter(g.port, {
          cle: cible.dataset.cle,
          type: cible.dataset.cvPort as Port['type'],
        })
      else if (
        g.precedent &&
        g.precedent.cle !== g.port.cle &&
        cible?.dataset.cle === g.port.cle
      )
        connecter(g.precedent, g.port)
      else {
        setPointeur(null)
        setMessage(
          `Choisissez ${g.port.type === 'sortie' ? 'une entrée à gauche d’une autre étape' : 'une sortie à droite d’une autre étape'}. Échap pour annuler.`,
        )
      }
    }
  }
  function retirerLien() {
    if (!lienSelectionne || lecture) return
    const { de, vers } = lienSelectionne
    onGraphe({
      ...graphe,
      liens: graphe.liens.filter((l) => l.de !== de || l.vers !== vers),
    })
    setLienSelectionne(null)
    setMessage(
      `La liaison entre « ${nom(de)} » et « ${nom(vers)} » est retirée.`,
    )
  }
  function debut(cle: string) {
    if (lecture) return
    const entrante = graphe.liens.some((l) => l.vers === cle)
    onGraphe({
      ...graphe,
      entree: cle,
      liens: graphe.liens.filter((l) => l.vers !== cle),
    })
    setMessage(
      `« ${nom(cle)} » est le point de départ.${entrante ? ' Sa liaison entrante a été retirée.' : ''}`,
    )
  }
  function ranger() {
    if (lecture) return
    const ordre: string[] = [],
      vus = new Set<string>()
    function chaine(de: string) {
      let cle: string | undefined = de
      while (cle && !vus.has(cle) && blocs.some((b) => b.cle === cle)) {
        vus.add(cle)
        ordre.push(cle)
        cle = graphe.liens.find((l) => l.de === cle)?.vers
      }
    }
    if (graphe.entree) chaine(graphe.entree)
    for (const b of blocs) chaine(b.cle)
    const positions = Object.fromEntries(
      ordre.map((cle, i) => [cle, positionParDefaut(i)]),
    )
    onGraphe({ ...graphe, positions })
    adapter(positions)
    setMessage(
      'Les étapes sont rangées. Les connexions et leur ordre restent identiques.',
    )
  }
  function deplacerClavier(e: KeyboardEvent, cle: string) {
    if (e.key === 'Escape') {
      annuler()
      return
    }
    if (lecture) return
    const mouvements: Record<string, Point> = {
        ArrowUp: { x: 0, y: -1 },
        ArrowDown: { x: 0, y: 1 },
        ArrowLeft: { x: -1, y: 0 },
        ArrowRight: { x: 1, y: 0 },
      },
      m = mouvements[e.key]
    if (m) {
      e.preventDefault()
      e.stopPropagation()
      const p = position(cle),
        pas = e.shiftKey ? 50 : 10
      onGraphe({
        ...graphe,
        positions: {
          ...graphe.positions,
          [cle]: { x: p.x + m.x * pas, y: p.y + m.y * pas },
        },
      })
    } else if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault()
      e.stopPropagation()
      onSupprimer(cle)
    }
  }
  const cibleSelectionnee = blocs.find((b) => b.cle === selection),
    aucuneEntree = !graphe.entree,
    clesConnues = new Set(blocs.map((b) => b.cle)),
    liensValides = graphe.liens.filter(
      (l) => clesConnues.has(l.de) && clesConnues.has(l.vers),
    ),
    horsLigne = blocs.filter(
      (b) =>
        b.cle !== graphe.entree &&
        !graphe.liens.some((l) => l.de === b.cle || l.vers === b.cle),
    ).length
  let liaisonTemporaire: string | undefined
  if (portActif && pointeur) {
    const fixe = pointPort(portActif)
    liaisonTemporaire =
      portActif.type === 'sortie'
        ? courbe(fixe, pointeur)
        : courbe(pointeur, fixe)
  }
  return (
    <div class="cv-root">
      <style>{stylesCanevasParcours}</style>
      <div class="cv-barre">
        <div class="cv-outils" role="group" aria-label="Outil du canevas">
          <button
            type="button"
            class={outil === 'selection' ? 'cv-actif' : ''}
            aria-pressed={outil === 'selection'}
            onClick={() => setOutil('selection')}
            title="Sélectionner et déplacer les étapes"
          >
            <Svg type="curseur" />
            <span>Sélection</span>
          </button>
          <button
            type="button"
            class={outil === 'main' ? 'cv-actif' : ''}
            aria-pressed={outil === 'main'}
            onClick={() => setOutil('main')}
            title="Déplacer la vue"
          >
            <Svg type="main" />
            <span>Déplacer la vue</span>
          </button>
        </div>
        <div class="cv-barre-droite">
          <button
            type="button"
            onClick={ranger}
            disabled={lecture || !blocs.length}
            title="Ranger les étapes sans modifier les connexions"
          >
            <Svg type="ranger" />
            <span>Ranger</span>
          </button>
          <button
            type="button"
            onClick={() => adapter()}
            disabled={!blocs.length}
            title="Afficher toutes les étapes"
          >
            <Svg type="adapter" />
            <span>Tout voir</span>
          </button>
        </div>
      </div>
      <div
        ref={surface}
        class={`cv-surface ${outil === 'main' ? 'cv-mode-main' : ''} ${glissement ? 'cv-glissement' : ''} ${survolDepot ? 'cv-depot-actif' : ''} ${portActif ? 'cv-connexion-active' : ''}`}
        tabIndex={0}
        role="region"
        aria-label="Canevas du parcours client"
        aria-describedby={identifiant.current + '-aide'}
        style={{
          backgroundSize: `${24 * camera.zoom}px ${24 * camera.zoom}px`,
          backgroundPosition: `${camera.x}px ${camera.y}px`,
        }}
        onPointerDown={(e) => {
          if (
            (e.target as HTMLElement).closest(
              '.cv-noeud,.cv-zoom,.cv-lien-action,.cv-etat-vide,.cv-lien',
            )
          )
            return
          commencerPan(e)
        }}
        onPointerMove={bouger}
        onPointerUp={terminer}
        onPointerCancel={annuler}
        onLostPointerCapture={() => {
          if (geste.current) annuler()
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.stopPropagation()
            annuler()
            setLienSelectionne(null)
            return
          }
          if (e.target !== e.currentTarget) return
          if (e.key === '+' || e.key === '=') {
            e.preventDefault()
            zoomer(camera.zoom * 1.15)
          } else if (e.key === '-') {
            e.preventDefault()
            zoomer(camera.zoom / 1.15)
          } else if (e.key === '0') {
            e.preventDefault()
            adapter()
          } else if (
            (e.key === 'Delete' || e.key === 'Backspace') &&
            lienSelectionne
          ) {
            e.preventDefault()
            retirerLien()
          } else if (e.key.startsWith('Arrow')) {
            e.preventDefault()
            setCamera({
              ...camera,
              x:
                camera.x +
                (e.key === 'ArrowLeft' ? 80 : e.key === 'ArrowRight' ? -80 : 0),
              y:
                camera.y +
                (e.key === 'ArrowUp' ? 80 : e.key === 'ArrowDown' ? -80 : 0),
            })
          }
        }}
        onDragOver={(e) => {
          if (
            !lecture &&
            Array.from(e.dataTransfer?.types ?? []).includes(
              'application/x-funo-bloc',
            )
          ) {
            e.preventDefault()
            e.dataTransfer!.dropEffect = 'copy'
            setSurvolDepot(true)
          }
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null))
            setSurvolDepot(false)
        }}
        onDrop={(e) => {
          e.preventDefault()
          setSurvolDepot(false)
          if (lecture) return
          const type = e.dataTransfer?.getData(
            'application/x-funo-bloc',
          ) as Bloc['bloc']
          if (!type || !Object.hasOwn(noms, type)) {
            setMessage('Choisissez une étape dans la bibliothèque du parcours.')
            return
          }
          const p = monde({ x: e.clientX, y: e.clientY })
          onAjouter(type, {
            x: Math.round(p.x - LARGEUR / 2),
            y: Math.round(p.y - HAUTEUR / 2),
          })
          setMessage(
            `Étape « ${noms[type]} » ajoutée. Reliez ses ports pour l’intégrer au parcours.`,
          )
        }}
      >
        <div
          class="cv-monde"
          style={{
            transform: `translate(${camera.x}px,${camera.y}px) scale(${camera.zoom})`,
          }}
        >
          <svg
            class="cv-liens"
            width="1"
            height="1"
            aria-label="Connexions entre les étapes"
          >
            <defs>
              <marker
                id={identifiant.current + '-fleche'}
                markerWidth="8"
                markerHeight="8"
                refX="6"
                refY="4"
                orient="auto"
                markerUnits="userSpaceOnUse"
              >
                <path
                  d="M1 1 6 4 1 7"
                  fill="none"
                  stroke="#ab97b3"
                  stroke-width="1.5"
                />
              </marker>
              <marker
                id={identifiant.current + '-fleche-active'}
                markerWidth="8"
                markerHeight="8"
                refX="6"
                refY="4"
                orient="auto"
                markerUnits="userSpaceOnUse"
              >
                <path
                  d="M1 1 6 4 1 7"
                  fill="none"
                  stroke="#391d44"
                  stroke-width="1.8"
                />
              </marker>
            </defs>
            {liensValides.map((l) => {
              const actif =
                  lienSelectionne?.de === l.de &&
                  lienSelectionne.vers === l.vers,
                d = courbe(
                  pointPort({ cle: l.de, type: 'sortie' }),
                  pointPort({ cle: l.vers, type: 'entree' }),
                )
              return (
                <g
                  key={`${l.de}-${l.vers}`}
                  class={`cv-lien ${actif ? 'cv-lien-actif' : ''}`}
                >
                  <path
                    d={d}
                    class="cv-lien-trait"
                    marker-end={`url(#${identifiant.current}-${actif ? 'fleche-active' : 'fleche'})`}
                  />
                  <path
                    d={d}
                    class="cv-lien-cible"
                    tabindex={0}
                    role="button"
                    aria-label={`Liaison de ${nom(l.de)} vers ${nom(l.vers)}`}
                    aria-pressed={actif}
                    onClick={(e) => {
                      e.stopPropagation()
                      setLienSelectionne(l)
                      setPortActif(null)
                      setPointeur(null)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        setLienSelectionne(l)
                        setPortActif(null)
                        setPointeur(null)
                        setMessage(
                          `Liaison sélectionnée : ${nom(l.de)} vers ${nom(l.vers)}.`,
                        )
                      } else if (
                        (e.key === 'Delete' || e.key === 'Backspace') &&
                        !lecture
                      ) {
                        e.preventDefault()
                        onGraphe({
                          ...graphe,
                          liens: graphe.liens.filter(
                            (x) => x.de !== l.de || x.vers !== l.vers,
                          ),
                        })
                        setLienSelectionne(null)
                      }
                    }}
                  />
                </g>
              )
            })}
            {liaisonTemporaire && (
              <path d={liaisonTemporaire} class="cv-lien-temporaire" />
            )}
          </svg>
          {blocs.map((b) => {
            const p = position(b.cle),
              estEntree = graphe.entree === b.cle,
              estSelectionne = selection === b.cle
            return (
              <div
                key={b.cle}
                class={`cv-noeud ${estSelectionne ? 'cv-noeud-selectionne' : ''} ${deplacement?.cle === b.cle ? 'cv-noeud-deplace' : ''}`}
                data-cv-node={b.cle}
                style={{ left: p.x, top: p.y, '--cv-accent': ACCENTS[b.bloc] }}
                role="group"
                aria-label={`Étape ${b.titre || noms[b.bloc]}${estEntree ? ', point de départ' : ''}`}
              >
                <button
                  type="button"
                  class={`cv-port cv-port-entree ${portActif?.cle === b.cle && portActif.type === 'entree' ? 'cv-port-actif' : ''}`}
                  data-cv-port="entree"
                  data-cle={b.cle}
                  aria-label={`Entrée de ${b.titre || noms[b.bloc]}`}
                  title="Entrée — connecter l’étape précédente"
                  disabled={lecture}
                  aria-pressed={
                    portActif?.cle === b.cle && portActif.type === 'entree'
                  }
                  onPointerDown={(e) =>
                    commencerLien(e, { cle: b.cle, type: 'entree' })
                  }
                  onClick={(e) => {
                    e.stopPropagation()
                    if (e.detail === 0)
                      choisirPort({ cle: b.cle, type: 'entree' })
                  }}
                >
                  <span />
                </button>
                <button
                  type="button"
                  class="cv-noeud-contenu"
                  onPointerDown={(e) => commencerNoeud(e, b.cle)}
                  onClick={() => {
                    onSelection(b.cle)
                    setLienSelectionne(null)
                  }}
                  onKeyDown={(e) => deplacerClavier(e, b.cle)}
                  aria-pressed={estSelectionne}
                  aria-label={`Configurer ${b.titre || noms[b.bloc]}`}
                  aria-describedby={identifiant.current + '-aide'}
                >
                  <span class="cv-noeud-entete">
                    <span class="cv-icone-noeud">{icone(b.bloc)}</span>
                    <span class="cv-noeud-titres">
                      <small>{noms[b.bloc]}</small>
                      <strong>{b.titre || noms[b.bloc]}</strong>
                    </span>
                    <span class="cv-poignee" aria-hidden="true">
                      ⠿
                    </span>
                  </span>
                  <span class="cv-noeud-description">{description(b)}</span>
                </button>
                <div class="cv-noeud-pied">
                  <div class="cv-badges">
                    {estEntree ? (
                      <span class="cv-badge cv-badge-depart">
                        <span aria-hidden="true">▶</span> Départ
                      </span>
                    ) : !liensValides.some(
                        (l) => l.de === b.cle || l.vers === b.cle,
                      ) ? (
                      <span class="cv-badge cv-badge-isole">À relier</span>
                    ) : (
                      <span class="cv-badge">Étape</span>
                    )}
                    {b.si && (
                      <span
                        class="cv-badge cv-badge-condition"
                        title="Cette étape s’exécute uniquement lorsque sa condition est vraie."
                      >
                        {resumeCondition(b.si)}
                      </span>
                    )}
                  </div>
                  <div class="cv-noeud-actions">
                    {!lecture && (
                      <>
                        <button
                          type="button"
                          title="Définir comme point de départ"
                          aria-label={`Définir ${b.titre || noms[b.bloc]} comme point de départ`}
                          disabled={estEntree}
                          onClick={() => debut(b.cle)}
                        >
                          <Svg type="depart" />
                        </button>
                        {(b.bloc === 'questions' ||
                          b.bloc === 'notification') && (
                          <button
                            type="button"
                            title="Dupliquer cette étape"
                            aria-label={`Dupliquer ${b.titre || noms[b.bloc]}`}
                            onClick={() => onDupliquer(b.cle)}
                          >
                            <Svg type="dupliquer" />
                          </button>
                        )}
                        <button
                          type="button"
                          title="Supprimer cette étape"
                          aria-label={`Supprimer ${b.titre || noms[b.bloc]}`}
                          onClick={() => onSupprimer(b.cle)}
                        >
                          <Svg type="supprimer" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  class={`cv-port cv-port-sortie ${portActif?.cle === b.cle && portActif.type === 'sortie' ? 'cv-port-actif' : ''}`}
                  data-cv-port="sortie"
                  data-cle={b.cle}
                  aria-label={`Sortie de ${b.titre || noms[b.bloc]}`}
                  title="Sortie — connecter l’étape suivante"
                  disabled={lecture}
                  aria-pressed={
                    portActif?.cle === b.cle && portActif.type === 'sortie'
                  }
                  onPointerDown={(e) =>
                    commencerLien(e, { cle: b.cle, type: 'sortie' })
                  }
                  onClick={(e) => {
                    e.stopPropagation()
                    if (e.detail === 0)
                      choisirPort({ cle: b.cle, type: 'sortie' })
                  }}
                >
                  <span />
                </button>
              </div>
            )
          })}
        </div>
        {!blocs.length && (
          <div class="cv-etat-vide">
            <span class="cv-vide-icone" aria-hidden="true">
              <Svg type="ranger" />
            </span>
            <h3>Composez votre parcours.</h3>
            <p>
              Glissez une étape depuis la bibliothèque, puis reliez les cartes
              dans l’ordre souhaité.
            </p>
            <small>
              Vous pouvez aussi ajouter les étapes avec leurs boutons.
            </small>
          </div>
        )}
        {portActif && (
          <div class="cv-connexion-invite">
            <span>
              Relier <strong>{nom(portActif.cle)}</strong> à{' '}
              {portActif.type === 'sortie'
                ? 'l’entrée d’une autre étape'
                : 'la sortie d’une autre étape'}
              .
            </span>
            <button
              type="button"
              aria-label="Annuler la connexion"
              onClick={() => {
                annuler()
                setMessage('Connexion annulée.')
              }}
            >
              <Svg type="fermer" />
            </button>
          </div>
        )}
        <div class="cv-zoom" role="group" aria-label="Zoom du canevas">
          <button
            type="button"
            onClick={() => zoomer(camera.zoom / 1.2)}
            aria-label="Réduire le zoom"
            disabled={camera.zoom <= 0.16}
          >
            −
          </button>
          <button
            type="button"
            onClick={() => zoomer(1)}
            aria-label="Rétablir le zoom à 100 pour cent"
            title="Revenir à 100 %"
          >
            {Math.round(camera.zoom * 100)} %
          </button>
          <button
            type="button"
            onClick={() => zoomer(camera.zoom * 1.2)}
            aria-label="Agrandir le zoom"
            disabled={camera.zoom >= 2}
          >
            ＋
          </button>
        </div>
        {lienSelectionne && (
          <div class="cv-lien-action">
            <span>
              <strong>{nom(lienSelectionne.de)}</strong>
              <span aria-hidden="true"> → </span>
              <strong>{nom(lienSelectionne.vers)}</strong>
            </span>
            {!lecture && (
              <button type="button" onClick={retirerLien}>
                <Svg type="supprimer" />
                Retirer la liaison
              </button>
            )}
            <button
              type="button"
              aria-label="Désélectionner la liaison"
              onClick={() => setLienSelectionne(null)}
            >
              <Svg type="fermer" />
            </button>
          </div>
        )}
      </div>
      <div class="cv-pied">
        <div class="cv-etat">
          <span
            class={
              aucuneEntree
                ? 'cv-indicateur cv-indicateur-attention'
                : 'cv-indicateur'
            }
          />
          <span>
            {blocs.length} étape{blocs.length > 1 ? 's' : ''} ·{' '}
            {liensValides.length} liaison{liensValides.length > 1 ? 's' : ''}
            {aucuneEntree && blocs.length
              ? ' · Point de départ à choisir'
              : horsLigne
                ? ` · ${horsLigne} à relier`
                : ''}
          </span>
        </div>
        <span class="cv-raccourcis">
          Déplacer la vue : fond ou molette · Zoom : Ctrl/⌘ + molette
        </span>
      </div>
      <p id={identifiant.current + '-aide'} class="cv-sr">
        Sélectionnez une étape pour la configurer. Déplacez une carte avec les
        flèches du clavier, ou Maj et les flèches pour un grand déplacement.
        Activez un port de sortie, puis un port d’entrée pour les relier. Échap
        annule la connexion. Sur le fond, les flèches déplacent la vue, plus et
        moins règlent le zoom, zéro affiche tout.
      </p>
      <div
        class={`cv-message ${message ? '' : 'cv-message-vide'}`}
        role="status"
        aria-live="polite"
      >
        {message ||
          (cibleSelectionnee
            ? `Sélection : ${cibleSelectionnee.titre || noms[cibleSelectionnee.bloc]}.`
            : 'Cliquez sur une carte pour régler cette étape.')}
      </div>
    </div>
  )
}

export const stylesCanevasParcours = `
.cv-root{height:100%;display:flex;flex-direction:column}.cv-root .cv-surface{flex:1;height:auto;min-height:0}.cv-root .cv-barre,.cv-root .cv-pied,.cv-root .cv-message{flex-shrink:0}
.cv-root{--cv-encre:#391d44;--cv-gris:#8c7794;--cv-ligne:#ece2f0;position:relative;min-width:0;border:1px solid #eadcf2;border-radius:16px;background:#fff;overflow:hidden;color:var(--cv-encre);font:13px/1.5 -apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif}.cv-root *{box-sizing:border-box}.cv-root button{font:inherit;cursor:pointer;min-height:0;border:0;background:none;color:inherit;border-radius:7px;padding:0;touch-action:manipulation}.cv-root button:disabled{cursor:default;opacity:.35}.cv-root button:hover:not(:disabled){background:#f5eef9}.cv-root button:focus-visible,.cv-surface:focus-visible{outline:3px solid #a0a1e3;outline-offset:3px}.cv-root button svg{flex-shrink:0}.cv-barre{min-height:58px;display:flex;align-items:center;justify-content:space-between;gap:12px;padding:9px 14px;background:#fff;border-bottom:1px solid var(--cv-ligne);position:relative;z-index:3}.cv-outils{display:flex;gap:3px;background:#f8f4f9;padding:3px;border-radius:9px}.cv-root .cv-outils button,.cv-barre-droite>button{display:flex;align-items:center;justify-content:center;gap:7px;min-height:33px;padding:7px 10px;font-size:11px;font-weight:550;color:#6b5a70;white-space:nowrap}.cv-root .cv-outils button.cv-actif{background:#fff;box-shadow:0 1px 4px #391d4412;color:#391d44}.cv-barre-droite{display:flex;align-items:center;gap:5px}.cv-root .cv-barre-droite>button{min-height:34px}.cv-surface{height:620px;min-height:600px;position:relative;overflow:hidden;background-color:#f9f6fc;background-image:radial-gradient(circle,#cfd0f1 .8px,transparent .8px);touch-action:none;cursor:grab;isolation:isolate}.cv-surface.cv-glissement{cursor:grabbing}.cv-surface.cv-depot-actif{box-shadow:inset 0 0 0 3px #9495e0;background-color:#f3ecf8}.cv-monde{position:absolute;left:0;top:0;transform-origin:0 0;will-change:transform}.cv-liens{position:absolute;left:0;top:0;overflow:visible;pointer-events:none;z-index:0}.cv-lien-trait{fill:none;stroke:#ab97b3;stroke-width:2}.cv-lien-actif .cv-lien-trait{stroke:#6466d2;stroke-width:2.5}.cv-lien-cible{fill:none;stroke:transparent;stroke-width:18;pointer-events:stroke;cursor:pointer}.cv-lien-cible:focus{outline:none;stroke:#7a7cd833;stroke-width:13}.cv-lien:hover .cv-lien-trait{stroke:#7476d7;stroke-width:2.5}.cv-lien-temporaire{fill:none;stroke:#7173d6;stroke-width:2.5;stroke-dasharray:7 5;pointer-events:none}.cv-noeud{position:absolute;width:248px;height:184px;background:#fff;border:1.5px solid #e9dbf1;border-radius:14px;box-shadow:0 3px 12px #391d4408;z-index:1;cursor:default;transition:border-color .12s,box-shadow .12s;overflow:visible}.cv-noeud:hover{border-color:#c0c0ed;box-shadow:0 5px 17px #391d4412}.cv-noeud.cv-noeud-selectionne{border-color:#7c7dd9;box-shadow:0 0 0 3px #acade72b,0 5px 18px #391d4410}.cv-noeud.cv-noeud-deplace{box-shadow:0 11px 32px #391d4429;transition:none;z-index:3}.cv-root .cv-noeud-contenu{display:block;width:100%;height:138px;text-align:left;padding:15px 15px 10px;border-radius:12px 12px 0 0;cursor:grab;color:var(--cv-encre)}.cv-root .cv-noeud-contenu:hover{background:#fff}.cv-glissement .cv-noeud-contenu{cursor:grabbing}.cv-mode-main .cv-noeud-contenu{cursor:grab}.cv-noeud-entete{display:flex;align-items:center;gap:11px}.cv-icone-noeud{display:grid;place-items:center;width:39px;height:39px;flex-shrink:0;color:var(--cv-accent);background:color-mix(in srgb,var(--cv-accent) 11%,#fff);border:1px solid color-mix(in srgb,var(--cv-accent) 18%,#fff);border-radius:10px}.cv-icone-noeud svg{width:21px;height:21px}.cv-noeud-titres{min-width:0;flex:1}.cv-noeud-titres small{display:block;color:#6b5a70;font-size:10px;line-height:1.35;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin-bottom:3px}.cv-noeud-titres strong{display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;font-size:14px;font-weight:650;line-height:1.4;letter-spacing:-.1px;overflow-wrap:anywhere;color:#403544}.cv-poignee{font-size:17px;color:#ccbfd1;align-self:flex-start;line-height:1.25}.cv-noeud-description{display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;font-size:12px;line-height:1.6;color:#786480;margin-top:13px;max-height:39px;font-weight:400;overflow-wrap:anywhere}.cv-noeud-pied{height:44px;display:flex;align-items:center;justify-content:space-between;gap:4px;border-top:1px solid #f5eff7;margin:0 12px}.cv-badges{display:flex;align-items:center;gap:4px;flex-wrap:wrap;min-width:0}.cv-badge{font-size:9px;color:#6b5a70;line-height:1.5;white-space:nowrap}.cv-badge-depart{padding:2px 5px;background:#f7f2f8;border-radius:4px;color:#6b5a70}.cv-badge-depart>span{font-size:6px}.cv-badge-isole{background:#fbf4e7;color:#af914f;padding:2px 5px;border-radius:4px}.cv-badge-condition{border:1px solid #ece0f3;color:#391d44;border-radius:4px;padding:1px 4px;font-size:9px}.cv-noeud-actions{display:flex;align-items:center;gap:0}.cv-root .cv-noeud-actions button{width:26px;height:31px;display:grid;place-items:center;color:#6b5a70;border-radius:5px}.cv-noeud-actions button svg{width:13px;height:13px}.cv-root .cv-noeud-actions button:hover:not(:disabled){color:#391d44;background:#f5eff9}.cv-root .cv-port{position:absolute;width:32px;height:42px;top:57px;display:grid;place-items:center;background:transparent!important;border:0!important;z-index:4;padding:0;border-radius:12px;touch-action:none}.cv-root .cv-port-entree{left:-17px}.cv-root .cv-port-sortie{right:-17px}.cv-port>span{display:block;width:12px;height:12px;border:2px solid #bcbdec;border-radius:50%;background:#fff;box-shadow:0 0 0 3px #f9f6fc;transition:transform .1s,background .1s,border-color .1s}.cv-port:hover:not(:disabled)>span,.cv-port:focus-visible>span,.cv-port.cv-port-actif>span{background:#7273d6;border-color:#7273d6;transform:scale(1.16);box-shadow:0 0 0 4px #ece0f3}.cv-connexion-active .cv-port>span{border-color:#8d8ede}.cv-etat-vide{position:absolute;left:50%;top:46%;transform:translate(-50%,-50%);width:min(380px,80%);text-align:center;pointer-events:none}.cv-vide-icone{display:grid;place-items:center;width:62px;height:62px;background:#f0e7f6;color:#6b5a70;border:1px solid #e6d6ef;border-radius:18px;margin:0 auto 22px}.cv-vide-icone svg{width:27px;height:27px}.cv-etat-vide h3{font-size:22px;font-weight:600;color:#74617c;margin:0 0 12px;letter-spacing:-.4px}.cv-etat-vide p{font-size:13px;color:#6b5a70;line-height:1.7;margin:0 0 18px}.cv-etat-vide small{font-size:10px;color:#6b5a70}.cv-zoom{position:absolute;bottom:17px;right:18px;background:#fff;border:1px solid #eadfef;border-radius:10px;box-shadow:0 3px 12px #391d4408;display:flex;padding:3px;z-index:5;cursor:default}.cv-root .cv-zoom button{min-width:32px;min-height:34px;font-size:15px;color:#6b5a70;font-weight:500}.cv-root .cv-zoom button:nth-child(2){font-size:10px;min-width:50px;color:#6b5a70}.cv-lien-action{position:absolute;bottom:19px;left:18px;max-width:calc(100% - 170px);background:#fff;border:1px solid #e3d2ee;border-radius:10px;box-shadow:0 4px 20px #391d4412;padding:8px 10px;display:flex;align-items:center;gap:12px;font-size:10px;z-index:5;cursor:default}.cv-lien-action>span{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#6b5a70;max-width:270px}.cv-lien-action strong{font-weight:500}.cv-root .cv-lien-action button{display:flex;align-items:center;gap:5px;white-space:nowrap;padding:5px;font-size:10px;color:#6b5a70;min-height:28px}.cv-lien-action button svg{width:13px;height:13px}.cv-connexion-invite{position:absolute;top:18px;left:50%;transform:translateX(-50%);display:flex;align-items:center;gap:14px;max-width:calc(100% - 36px);background:#fdfcfe;border:1px solid #e4d3ee;border-radius:10px;padding:10px 13px;font-size:11px;color:#6b5a70;box-shadow:0 4px 18px #391d4409;z-index:5;cursor:default}.cv-connexion-invite>span{min-width:0}.cv-root .cv-connexion-invite button{width:25px;height:25px;display:grid;place-items:center;flex-shrink:0}.cv-pied{display:flex;align-items:center;justify-content:space-between;gap:15px;min-height:38px;padding:10px 16px;border-top:1px solid var(--cv-ligne);background:#fefdfe;color:#6b5a70;font-size:10px}.cv-etat{display:flex;align-items:center;gap:7px;white-space:nowrap}.cv-indicateur{width:5px;height:5px;border-radius:50%;background:#c1b1c7}.cv-indicateur-attention{background:#cfb77f}.cv-raccourcis{font-size:9px;color:#6b5a70;text-align:right}.cv-message{font-size:10px;line-height:1.6;padding:10px 16px;color:#846f8d;background:#fbf8fc;border-top:1px solid #f1eaf4;min-height:36px}.cv-message-vide{color:#6b5a70;background:#fff;border-color:#f5eff7}.cv-sr{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0)}
@media(max-width:850px){.cv-barre{padding:9px;gap:8px}.cv-root .cv-outils button,.cv-barre-droite>button{padding:7px 8px;font-size:10px}.cv-raccourcis{display:none}.cv-lien-action>span{display:none}.cv-noeud-actions button{width:28px!important}.cv-surface{height:620px}.cv-pied{font-size:9px}.cv-connexion-invite{font-size:10px}}
@media(max-width:550px){.cv-barre{min-height:54px;flex-wrap:wrap;justify-content:space-between}.cv-root .cv-outils button,.cv-barre-droite>button{min-height:34px}.cv-outils button span{display:none}.cv-root .cv-outils button{width:37px}.cv-barre-droite{gap:1px}.cv-barre-droite>button{padding:6px 7px}.cv-barre-droite>button span{font-size:9px}.cv-surface{height:620px;min-height:600px}.cv-lien-action{bottom:67px;left:12px;max-width:calc(100% - 24px);padding:7px}.cv-lien-action>span{display:block;max-width:125px}.cv-zoom{bottom:14px;right:12px}.cv-root .cv-zoom button{min-height:36px}.cv-pied,.cv-message{padding:10px 12px}.cv-connexion-invite{top:12px;max-width:calc(100% - 22px);width:max-content}.cv-etat-vide h3{font-size:19px}.cv-etat-vide p{font-size:12px}}
@media(prefers-reduced-motion:reduce){.cv-root *{transition:none!important}}
`
